import React, { useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import {
  Edit,
  CalendarMonth,
  AccessTimeRounded,
  HelpOutlineRounded,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import moment from "moment";
import { EditOTPreApproval } from "./EditOTPreApproval";
import { tooltipStyleProps } from "services/constants/Styles";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestStatusTimeline } from "components/DesignSystem/RequestStatusTimeline";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import RequestDetailsSection from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { normalizeApprovalPersonFields } from "pages/TeamZone/Approve/displayNameUtils";

const formatOTDateTime = (value) => {
  if (!value) return "";
  const parsed = moment(
    value,
    ["DD-MM-YYYY, HH:mm", "DD-MM-YYYY, hh:mm A", moment.ISO_8601],
    true,
  );
  return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : value;
};

export const ViewOTPreApproval = ({
  open = true,
  onClose,
  editOTPreAppDetails,
  reCallApi,
  setGetId,
  setHandleLoader,
  screenAccess,
  otType = "PreOT",
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const displayData = normalizeApprovalPersonFields({
    ...editOTPreAppDetails,
    employee_name:
      editOTPreAppDetails?.employee_name ||
      editOTPreAppDetails?.user_name ||
      editOTPreAppDetails?.username ||
      userDetails?.first_name,
    employee_id:
      editOTPreAppDetails?.employee_id ||
      editOTPreAppDetails?.emp_id ||
      userDetails?.employee_id,
  });
  const employeeName = displayData.employee_name || "N/A";
  const employeeId =
    editOTPreAppDetails?.employee_id ||
    editOTPreAppDetails?.emp_id ||
    userDetails?.employee_id ||
    "-";
  const department =
    editOTPreAppDetails?.department ||
    editOTPreAppDetails?.department_name ||
    userDetails?.department_name ||
    "-";
  const designation =
    editOTPreAppDetails?.designation ||
    editOTPreAppDetails?.designation_name ||
    userDetails?.designation ||
    "-";
  const statusFlowData = {
    ...editOTPreAppDetails,
    applied_by: displayData.applied_by || editOTPreAppDetails?.createdby || employeeName,
    applied_on: formatOTDateTime(
      editOTPreAppDetails?.applied_on ||
        editOTPreAppDetails?.applied_date ||
        editOTPreAppDetails?.created_at ||
        editOTPreAppDetails?.created_date,
    ),
    approved_by: displayData.approved_by,
    approved_at: formatOTDateTime(
      editOTPreAppDetails?.approved_at || editOTPreAppDetails?.approved_on,
    ),
    rejected_by: displayData.rejected_by,
    rejected_at: formatOTDateTime(
      editOTPreAppDetails?.rejected_at || editOTPreAppDetails?.rejected_on,
    ),
    cancelled_by: displayData.cancelled_by,
    cancelled_at: formatOTDateTime(
      editOTPreAppDetails?.cancelled_at ||
        editOTPreAppDetails?.cancelled_on ||
        editOTPreAppDetails?.canceled_at ||
        editOTPreAppDetails?.canceled_on,
    ),
    cancelled_reason:
      editOTPreAppDetails?.cancelled_reason ||
      editOTPreAppDetails?.cancel_reason ||
      editOTPreAppDetails?.canceled_reason,
  };

  const handleEditOTPreApproval = () => {
    setIsEditPanelOpen(true);
  };

  const handleEditClosePanel = () => {
    setIsEditPanelOpen(false);
  };

  const handleCancelRequest = async () => {
    setCancelling(true);
    const response = await customAPI(
      `${domain}My_Zone/Apply/Over_time/myworkspace-ot-approval/?company_id=${requestCompanyId}`,
      "PUT",
      {
        ot_type: otType,
        id: editOTPreAppDetails?.id,
        manual_ot_id: editOTPreAppDetails?.id,
        status: "Cancelled",
      },
    );
    setCancelling(false);
    setShowCancelDialog(false);
    if (response.ok) {
      showToast("success", response?.responseDetails?.message || "Request cancelled");
      reCallApi(true);
      onClose();
    } else {
      showToast("error", response?.message);
    }
  };

  // Edit/Cancel used to always render for a Pending request and only block
  // on click (toast) when the viewer lacked edit access -- a view-only user
  // saw both actions and hit "Access denied" after opening them. Both now
  // require screenAccess (the same "My Request" editor permission the New
  // Request button on Overtime/index.js is gated on) before they render at
  // all, matching how TeamZone Approve's own detail panels hide rather than
  // disable an action the viewer isn't allowed to take.
  const editAction =
    screenAccess && editOTPreAppDetails?.status === "Pending" ? (
      <Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
        <IconButton onClick={handleEditOTPreApproval}>
          <Edit />
        </IconButton>
      </Tooltip>
    ) : null;

  const canCancel = screenAccess && editOTPreAppDetails?.status === "Pending";

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={onClose}
        title="Overtime Request"
        subtitle="Request details and status"
        headerActions={editAction}
        footer={
          canCancel ? (
            <RequestCancelButton onClick={() => setShowCancelDialog(true)} />
          ) : null
        }
      >
        <ConfirmationDialog
          open={showCancelDialog}
          onClose={() => setShowCancelDialog(false)}
          onConfirm={handleCancelRequest}
          title="Cancel Request"
          message="Are you sure you want to cancel this request?"
          confirmText="Confirm"
          cancelText="Cancel"
          disableAction={cancelling}
        />
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <RequestDetailHeader
            userDetails={{
              image_url: editOTPreAppDetails?.image_url || userDetails?.image_url,
              employee_id: employeeId,
              user_name: employeeName,
              department_name: department,
              designation,
            }}
            status={editOTPreAppDetails?.status}
            colorCode={editOTPreAppDetails?.color_code}
            variant="employeeDetailsCard"
          />

          <RequestDetailsSection
            fields={[
              {
                icon: CalendarMonth,
                label: "Overtime Date / Period",
                value: editOTPreAppDetails?.ot_date || "-",
                accent: detailAccentPalette.pink,
              },
              {
                icon: AccessTimeRounded,
                label: "Applied On",
                value: statusFlowData.applied_on || "-",
                accent: detailAccentPalette.blue,
              },
              {
                icon: AccessTimeRounded,
                label: "Duration in Hrs",
                value: editOTPreAppDetails?.ot_duration || "-",
                accent: detailAccentPalette.blue,
              },
              {
                icon: CalendarMonth,
                label: "Applicable Timing",
                value: editOTPreAppDetails?.ot_consideration || "-",
                accent: detailAccentPalette.green,
              },
              {
                icon: HelpOutlineRounded,
                label: "Reason",
                value: editOTPreAppDetails?.reason || "-",
                accent: detailAccentPalette.orange,
                multiline: true,
              },
            ]}
          />

          <RequestStatusTimeline endDate={editOTPreAppDetails?.ot_date} data={statusFlowData} hidePendingStep />

        </Box>
      </GlobalSidePanel>
      {isEditPanelOpen ? (
        <EditOTPreApproval
          open={isEditPanelOpen}
          onClose={handleEditClosePanel}
          editOTPreAppDetails={editOTPreAppDetails}
          reCallApi={reCallApi}
          setGetId={setGetId}
          setHandleLoader={setHandleLoader}
          otType={otType}
        />
      ) : null}
    </>
  );
};
